import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { NotificationKind, UseNotifyReturn, WidgetNotification } from './types'

// ─── Context ───────────────────────────────────────────────────────────────────

interface NotificationContextValue {
  notifications: WidgetNotification[]
  push: UseNotifyReturn['push']
  dismiss: UseNotifyReturn['dismiss']
  clear: UseNotifyReturn['clear']
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

// ─── Provider-less ref so the QueryCache / MutationCache callbacks — which run outside the
// React tree — can still push notifications via this module's exports. Set on mount by the
// Provider. Null when no provider is mounted (fallback to console.warn). ───────────────

let currentPush: UseNotifyReturn['push'] | null = null
let currentDismiss: UseNotifyReturn['dismiss'] | null = null
let currentClear: UseNotifyReturn['clear'] | null = null

/**
 * Push a notification from outside React (e.g. from a TanStack `QueryCache.onError`). Silently
 * no-ops when no `<NotificationProvider>` is mounted — error handlers call this eagerly, the
 * absence of a mounted provider is not itself a failure.
 */
export function pushNotificationExternal(n: Omit<WidgetNotification, 'id'>): string | null {
  if (!currentPush) return null
  return currentPush(n)
}

export function dismissNotificationExternal(id: string): void {
  currentDismiss?.(id)
}

// ─── Provider ──────────────────────────────────────────────────────────────────

const DEFAULT_AUTO_DISMISS_MS: Record<NotificationKind, number | null> = {
  success: 4000,
  info: 6000,
  warning: 8000,
  // Errors require user acknowledgement by default — null means "stay until manually dismissed".
  error: null,
}

const MAX_VISIBLE = 3

export interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<WidgetNotification[]>([])
  const timeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    const handle = timeoutsRef.current.get(id)
    if (handle) {
      clearTimeout(handle)
      timeoutsRef.current.delete(id)
    }
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const push = useCallback<UseNotifyReturn['push']>((incoming) => {
    const id = incoming.id ?? (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `rqd-notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)

    const autoDismissMs = incoming.autoDismissMs === undefined
      ? DEFAULT_AUTO_DISMISS_MS[incoming.kind]
      : incoming.autoDismissMs

    const next: WidgetNotification = {
      id,
      kind: incoming.kind,
      title: incoming.title,
      detail: incoming.detail,
      autoDismissMs,
    }

    setNotifications(prev => {
      // De-dupe identical title+kind so retry-storms don't stack ten identical toasts.
      const withoutDupe = prev.filter(n => !(n.kind === next.kind && n.title === next.title))
      const queue = [...withoutDupe, next]
      // Cap the visible count — drop the oldest beyond the cap.
      while (queue.length > MAX_VISIBLE) {
        const dropped = queue.shift()
        if (dropped) {
          const h = timeoutsRef.current.get(dropped.id)
          if (h) { clearTimeout(h); timeoutsRef.current.delete(dropped.id) }
        }
      }
      return queue
    })

    if (typeof autoDismissMs === 'number' && autoDismissMs > 0) {
      const handle = setTimeout(() => dismiss(id), autoDismissMs)
      timeoutsRef.current.set(id, handle)
    }

    return id
  }, [dismiss])

  const clear = useCallback(() => {
    for (const handle of timeoutsRef.current.values()) clearTimeout(handle)
    timeoutsRef.current.clear()
    setNotifications([])
  }, [])

  // Expose the push/dismiss/clear callbacks to the module-level ref so non-React callers (the
  // TanStack Query caches) can drive the surface.
  useEffect(() => {
    currentPush = push
    currentDismiss = dismiss
    currentClear = clear
    return () => {
      // Null-out on unmount so stale callbacks don't resolve against a torn-down React tree.
      if (currentPush === push) currentPush = null
      if (currentDismiss === dismiss) currentDismiss = null
      if (currentClear === clear) currentClear = null
    }
  }, [push, dismiss, clear])

  useEffect(() => {
    // Clear all pending timeouts when the provider unmounts.
    const timeouts = timeoutsRef.current
    return () => {
      for (const handle of timeouts.values()) clearTimeout(handle)
      timeouts.clear()
    }
  }, [])

  const value = useMemo<NotificationContextValue>(
    () => ({ notifications, push, dismiss, clear }),
    [notifications, push, dismiss, clear],
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Push / dismiss / clear notifications. Usable from any component inside `<ReqdeskProvider>` —
 * hosts can reuse the same stack for their own in-widget messages via `useReqdesk().` wrappers.
 */
export function useNotify(): UseNotifyReturn {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    // Graceful fallback: when the provider is absent, no-op everything rather than throw. This
    // lets the widget's public API surface (used by hosts in unusual contexts, e.g. Storybook)
    // not explode when it's called outside the widget tree.
    return {
      push: () => '',
      dismiss: () => { /* noop */ },
      clear: () => { /* noop */ },
    }
  }
  return { push: ctx.push, dismiss: ctx.dismiss, clear: ctx.clear }
}

function useNotifications(): WidgetNotification[] {
  const ctx = useContext(NotificationContext)
  return ctx?.notifications ?? []
}

// ─── Renderer ──────────────────────────────────────────────────────────────────

export interface NotificationStackProps {
  /** Override the dismiss-button accessible name (defaults to "Dismiss"). */
  dismissLabel?: string
}

/**
 * Render the notification queue at the top of the widget panel. Accessible:
 *   - `aria-live="polite"` so screen readers announce new items without interrupting.
 *   - Per-item `role="alert"` on errors (critical), `role="status"` on others.
 *   - Keyboard-dismissible via the ✕ button.
 *
 * Place inside the widget's shadow DOM — no host-page leakage.
 */
export function NotificationStack({ dismissLabel = 'Dismiss' }: NotificationStackProps) {
  const notifications = useNotifications()
  const ctx = useContext(NotificationContext)

  if (!ctx || notifications.length === 0) return null

  return (
    <div className="rqd-notification-stack" aria-live="polite" aria-atomic="false">
      {notifications.map(n => (
        <div
          key={n.id}
          className={`rqd-notification rqd-notification-${n.kind}`}
          role={n.kind === 'error' ? 'alert' : 'status'}
        >
          <span className="rqd-notification-icon" aria-hidden="true">{iconFor(n.kind)}</span>
          <div className="rqd-notification-body">
            <p className="rqd-notification-title">{n.title}</p>
            {n.detail && n.detail.length > 0 && (
              <ul className="rqd-notification-list">
                {n.detail.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="rqd-notification-dismiss"
            onClick={() => ctx.dismiss(n.id)}
            aria-label={dismissLabel}
          >
            {'\u2715'}
          </button>
        </div>
      ))}
    </div>
  )
}

function iconFor(kind: NotificationKind): string {
  switch (kind) {
    case 'error': return '\u26A0'     // ⚠
    case 'warning': return '\u26A0'
    case 'info': return '\u2139'      // ℹ
    case 'success': return '\u2713'   // ✓
  }
}
